// First-run demo seed — DEV ONLY (guarded by __DEV__ in db/index.ts, so pilot
// and store builds start empty). Real installs get seedBaselineIfNeeded instead.

import {
  MOCK_GOALS,
  MOCK_HOUSEHOLD,
  MOCK_POLICIES,
  MOCK_REMINDERS,
  MOCK_SPENT_THIS_MONTH_GR,
} from '@/mocks/data';
import type { BudgetEntry, CategoryId, GoalContribution, Household } from '@/types/domain';
import { todayIso, uuid } from '@/utils/format';

import { TABLES, type DocStore } from './types';

export const KV_HOUSEHOLD = 'household';
const KV_SEEDED = 'seeded_v1';

const SEED_WEIGHTS = [3, 1, 2, 6, 1, 2, 4, 1, 3, 2, 5, 1, 2, 3, 1];
const SEED_CATEGORIES: CategoryId[] = ['FOOD', 'BILLS', 'FUEL', 'KIDS', 'HOME', 'FOOD', 'INSTALLMENTS', 'FUN', 'FOOD'];

function isoAt(date: Date, hour: number, minute: number): string {
  const d = new Date(date);
  d.setHours(hour, minute, 0, 0);
  return d.toISOString();
}

/** Spread the mock month-to-date spending over real DB entries (uneven, like life) */
function buildSeedEntries(now: Date): BudgetEntry[] {
  const day = now.getDate();
  const entries: BudgetEntry[] = [];
  const base = (categoryId: CategoryId, amountGr: number, entryDate: Date, hour: number, minute: number, member: string): BudgetEntry => ({
    id: uuid(),
    householdId: MOCK_HOUSEHOLD.id,
    categoryId,
    amountGr,
    note: null,
    entryDate: todayIso(entryDate),
    authorMemberId: member,
    createdAt: isoAt(entryDate, hour, minute),
    updatedAt: isoAt(entryDate, hour, minute),
  });

  // The two entries visible on the Stitch mock: Jedzenie −89 zł today 14:20, Paliwo −45,50 zł yesterday 18:05
  const today = new Date(now);
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  const fixed: BudgetEntry[] = [base('FOOD', 8_900, today, 14, 20, 'mem-0001')];
  let remaining = MOCK_SPENT_THIS_MONTH_GR - 8_900;
  if (yesterday.getMonth() === now.getMonth()) {
    fixed.push(base('FUEL', 4_550, yesterday, 18, 5, 'mem-0002'));
    remaining -= 4_550;
  }

  // Spread the rest over days 1..day-2 with the uneven weight pattern
  const spreadDays = Math.max(0, day - 2);
  if (spreadDays === 0 || remaining <= 0) {
    return [...fixed].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  }
  const weights = Array.from({ length: spreadDays }, (_, i) => SEED_WEIGHTS[i % SEED_WEIGHTS.length]);
  const weightSum = weights.reduce((a, b) => a + b, 0);
  let allocated = 0;
  weights.forEach((w, i) => {
    const d = new Date(now.getFullYear(), now.getMonth(), i + 1);
    const amount = i === spreadDays - 1
      ? remaining - allocated
      : Math.round((remaining * w) / weightSum / 100) * 100;
    allocated += amount;
    if (amount > 0) {
      entries.push(
        base(SEED_CATEGORIES[i % SEED_CATEGORIES.length], amount, d, 10 + (i % 9), 7 * (i % 8), i % 3 === 0 ? 'mem-0002' : 'mem-0001'),
      );
    }
  });

  return [...entries, ...fixed].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
}

/** Demo contributions matching the goal-details mock (Ania 500 zł wczoraj, Ty 1 000 zł 3 dni temu) */
function buildSeedContributions(now: Date): GoalContribution[] {
  const at = (daysAgo: number, hour: number) => {
    const d = new Date(now);
    d.setDate(d.getDate() - daysAgo);
    return d;
  };
  const goalId = MOCK_GOALS[0].id;
  return [
    {
      id: uuid(),
      goalId,
      amountGr: 50_000,
      memberId: 'mem-0002',
      date: todayIso(at(1, 12)),
      createdAt: isoAt(at(1, 12), 12, 30),
    },
    {
      id: uuid(),
      goalId,
      amountGr: 100_000,
      memberId: 'mem-0001',
      date: todayIso(at(3, 9)),
      createdAt: isoAt(at(3, 9), 9, 15),
    },
  ];
}

/**
 * Baseline for a FRESH real install (pilot/production): every screen assumes a
 * household exists (entries need householdId, budget/verdicts read its budget),
 * so an empty-but-valid one is created instead of demo data. Budget starts at 0
 * — the Budżet tab shows a "set your budget" card until the user fills it in.
 */
function makeBaselineHousehold(): Household {
  return {
    id: uuid(),
    name: 'Mój dom',
    monthlyBudgetGr: 0,
    inviteCode: null,
    // memberNameOf renders mem-0001 as "Ty" — the local user is always the owner
    members: [{ id: 'mem-0001', role: 'OWNER', firstName: 'Ty', accountId: null, birthYear: null }],
    declarations: { ownsHome: false, hasMortgage: false, hasCar: false, hasKids: false, runsBusiness: false },
    updatedAt: new Date().toISOString(),
  };
}

export async function seedBaselineIfNeeded(store: DocStore): Promise<void> {
  if (await store.getKv(KV_HOUSEHOLD)) return;
  await store.setKv(KV_HOUSEHOLD, JSON.stringify(makeBaselineHousehold()));
}

/**
 * INTENT: on a REAL login, wipe leftover demo data and start from a clean
 * baseline. The president logged in with Google and still saw the demo
 * household (2026-07-18) — demo data seeded earlier (a dev-mode web/preview run
 * writes KV_SEEDED='1') persists forever, because seedBaselineIfNeeded bails
 * when any household exists. KV_SEEDED is set ONLY by the demo seed, never by a
 * real user, so it is a safe "this store holds demo data" flag: gating on it
 * guarantees we never touch a real guest's own data. Returns true if it wiped.
 */
export async function clearDemoDataIfPresent(store: DocStore): Promise<boolean> {
  if ((await store.getKv(KV_SEEDED)) !== '1') return false;
  for (const table of Object.values(TABLES)) {
    const rows = await store.list<{ id: string }>(table);
    for (const row of rows) await store.remove(table, row.id);
  }
  await store.setKv(KV_SEEDED, '0');
  await store.setKv(KV_HOUSEHOLD, JSON.stringify(makeBaselineHousehold()));
  return true;
}

export async function seedIfNeeded(store: DocStore): Promise<void> {
  if ((await store.getKv(KV_SEEDED)) === '1') return;

  const now = new Date();
  await store.setKv(KV_HOUSEHOLD, JSON.stringify(MOCK_HOUSEHOLD));
  for (const p of MOCK_POLICIES) await store.upsert(TABLES.policies, p.id, p);
  for (const r of MOCK_REMINDERS) await store.upsert(TABLES.reminders, r.id, r);
  for (const g of MOCK_GOALS) await store.upsert(TABLES.goals, g.id, g);
  for (const c of buildSeedContributions(now)) await store.upsert(TABLES.contributions, c.id, c);
  for (const e of buildSeedEntries(now)) await store.upsert(TABLES.entries, e.id, e);

  await store.setKv(KV_SEEDED, '1');
}
